// imean-front/src/pages/Analytics/MediaInsightsPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Layout, Card, Space, Typography, Row, Col, Select, Spin, Button, message, Avatar, Tooltip } from "antd";
import { PictureOutlined, LineChartOutlined, ArrowLeftOutlined, QuestionCircleOutlined, EyeOutlined, SaveOutlined, ShareAltOutlined, MessageOutlined, HeartOutlined, UserOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { fetchLiveMediaInsights, fetchMediaSnapshotsSeries } from "../../api/analytics";
import LineChart from "../../components/Charts/LineChart";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ImeanHeader } from "../../components/Header/Header";

const { Header, Content } = Layout;
const { Title, Text } = Typography;
const { Option } = Select;

function toDailySeries(points, key) {
  const map = new Map();
  for (const p of points) {
    const day = dayjs(p.ts || p.date).format("YYYY-MM-DD");
    const prev = map.get(day);
    if (!prev || dayjs(p.ts || p.date).isAfter(prev.ts)) {
      map.set(day, { ts: p.ts || p.date, value: typeof p[key] === "number" ? p[key] : null });
    }
  }
  const days = [...map.keys()].sort();
  if (days.length === 0) return [];
  const from = dayjs(days[0]);
  const to = dayjs(days[days.length - 1]);
  const out = [];
  let last = null;
  let cursor = from;
  while (cursor.isBefore(to) || cursor.isSame(to)) {
    const d = cursor.format("YYYY-MM-DD");
    const rec = map.get(d);
    if (rec && typeof rec.value === "number") last = rec.value;
    out.push({ date: cursor.format("DD.MM"), value: rec ? rec.value : last });
    cursor = cursor.add(1, "day");
  }
  return out;
}

const PRESETS = [
  { key: "7d", labelKey: "mediaInsights.periodWeek", ig: "week", fromShift: 6 },
  { key: "30d", labelKey: "mediaInsights.period30d", ig: "days_28", fromShift: 29 },
];

const StatCard = ({ icon, title, help, value, subtitle }) => (
  <Card size="small" bordered={false} style={{ boxShadow: "0 1px 3px rgba(15,23,42,0.08)", borderRadius: 12 }}>
    <Space direction="vertical" style={{ width: "100%" }}>
      <Space align="center" style={{ justifyContent: "space-between", width: "100%" }}>
        <Space align="center">
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: "linear-gradient(135deg, rgba(124,58,237,0.15), rgba(99,102,241,0.15))",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#7c3aed",
            }}
          >
            {icon}
          </div>
          <Text type="secondary" style={{ fontSize: 12 }}>{title}</Text>
        </Space>
        {help && (
          <Tooltip title={help} placement="topRight">
            <QuestionCircleOutlined style={{ color: "#94a3b8" }} />
          </Tooltip>
        )}
      </Space>
      <Text strong style={{ fontSize: 20, color: "#111827" }}>{value}</Text>
      {subtitle && (
        <Text type="secondary" style={{ fontSize: 11 }}>
          {subtitle}
        </Text>
      )}
    </Space>
  </Card>
);

function MediaInsightsPage() {
  const { mediaId } = useParams();
  const [sp, setSp] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const presetKey = sp.get("p") || "7d";
  const preset = PRESETS.find((p) => p.key === presetKey) || PRESETS[0];

  const [live, setLive] = useState(null);
  const [seriesLikes, setSeriesLikes] = useState([]);
  const [seriesComments, setSeriesComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 680 : false
  );

  useEffect(() => {
    function onResize() {
      setIsMobile(window.innerWidth < 680);
    }
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const range = useMemo(() => {
    const to = dayjs();
    const from = to.clone().subtract(preset.fromShift, "day");
    return { from: from.format("YYYY-MM-DD"), to: to.format("YYYY-MM-DD") };
  }, [presetKey]);

  useEffect(() => {
    let active = true;
    async function load() {
      setLoading(true);
      try {
        const [liveRes, snapRes] = await Promise.all([
          fetchLiveMediaInsights({ media_id: mediaId, period: preset.ig }),
          fetchMediaSnapshotsSeries({ media_id: mediaId, from: range.from, to: range.to }),
        ]);
        if (!active) return;
        setLive(liveRes);
        setSeriesLikes(toDailySeries(snapRes.points || [], "like_count"));
        setSeriesComments(toDailySeries(snapRes.points || [], "comments_count"));
      } catch (e) {
        if (active) message.error(e.message || "Ошибка загрузки инсайтов");
      } finally {
        if (active) setLoading(false);
      }
    }
    load();
    return () => {
      active = false;
    };
  }, [mediaId, presetKey, range.from, range.to]);

  const m = live?.metrics || {};
  const mediaInfo = live?.media || {};
  const mediaThumb = mediaInfo.thumbnail_url || mediaInfo.media_url || null;
  const presetLabel = t(preset.labelKey);
  const formatNumber = (n) => {
    if (n == null || n === "" || isNaN(Number(n))) return "-";
    return Number(n).toLocaleString("ru-RU");
  };

  return (
    <>
      <ImeanHeader initialDark={true} />
      <Layout style={{ minHeight: "100vh", background: "linear-gradient(180deg, #ffffff, #faf5ff)" }}>
        <Header
          style={{
            position: "sticky",
            top: 0,
            zIndex: 10,
            background: "rgba(255,255,255,0.85)",
            backdropFilter: "blur(6px)",
            borderBottom: "1px solid #f0f0f0",
            padding: 0, // Убираем padding из Header
          }}
        >
          {/* Добавляем контейнер с такими же стилями, как у Content */}
          <div style={{
            maxWidth: 1100,
            margin: "0 auto",
            width: "100%",
            padding: isMobile ? "0 16px" : "0 24px"
          }}>
            <Row align="middle" justify="space-between">
              <Space align="center" size={12}>
                <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
                  {t("mediaInsights.back")}
                </Button>
                <Avatar src={mediaThumb || undefined} icon={<PictureOutlined />} size={36} shape="square" />
                <div>
                  <Title level={4} style={{ margin: 0, lineHeight: 1.1 }}>
                    {t("mediaInsights.title")}
                  </Title>
                </div>
              </Space>

              <Space>
                {mediaInfo.permalink && (
                  <Button type="link" size="small" href={mediaInfo.permalink} target="_blank" rel="noreferrer">
                    {t("mediaInsights.openPost")}
                  </Button>
                )}
                <Select
                  value={presetKey}
                  onChange={(val) => setSp({ p: val }, { replace: true })}
                  style={{ minWidth: 160 }}
                  popupMatchSelectWidth={false}
                >
                  {PRESETS.map((p) => (
                    <Option key={p.key} value={p.key}>
                      {t(p.labelKey)}
                    </Option>
                  ))}
                </Select>
              </Space>
            </Row>
          </div>
        </Header>

        <Content style={{ maxWidth: 1100, margin: "24px auto", width: "100%", padding: "0 16px" }}>
          <Spin spinning={loading}>
            {live && (
              <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={12} md={6}>
                  <StatCard
                    icon={<EyeOutlined />}
                    title={t("mediaInsights.metrics.views.title")}
                    help={t("mediaInsights.metrics.views.description")}
                    value={formatNumber(m.view_count ?? m.views)}
                    subtitle={`за ${presetLabel}`}
                  />
                </Col>
                <Col xs={12} md={6}>
                  <StatCard
                    icon={<LineChartOutlined />}
                    title={t("mediaInsights.metrics.reach.title")}
                    help={t("mediaInsights.metrics.reach.description")}
                    value={formatNumber(m.reach)}
                    subtitle={`за ${presetLabel}`}
                  />
                </Col>
                <Col xs={12} md={6}>
                  <StatCard
                    icon={<LineChartOutlined />}
                    title={t("mediaInsights.metrics.total_interactions.title")}
                    help={t("mediaInsights.metrics.total_interactions.description")}
                    value={formatNumber(m.total_interactions)}
                    subtitle={`за ${presetLabel}`}
                  />
                </Col>
                <Col xs={12} md={6}>
                  <StatCard
                    icon={<SaveOutlined />}
                    title={t("mediaInsights.metrics.saved.title")}
                    help={t("mediaInsights.metrics.saved.description")}
                    value={formatNumber(m.saved)}
                    subtitle={`за ${presetLabel}`}
                  />
                </Col>
                <Col xs={12} md={6}>
                  <StatCard
                    icon={<ShareAltOutlined />}
                    title={t("mediaInsights.metrics.shares.title")}
                    help={t("mediaInsights.metrics.shares.description")}
                    value={formatNumber(m.shares)}
                    subtitle={`за ${presetLabel}`}
                  />
                </Col>
                <Col xs={12} md={6}>
                  <StatCard
                    icon={<UserOutlined />}
                    title={t("mediaInsights.metrics.profile_visits.title")}
                    help={t("mediaInsights.metrics.profile_visits.description")}
                    value={formatNumber(m.profile_visits)}
                    subtitle={`за ${presetLabel}`}
                  />
                </Col>
                <Col xs={12} md={6}>
                  <StatCard
                    icon={<MessageOutlined />}
                    title={t("mediaInsights.metrics.comments.title")}
                    help={t("mediaInsights.metrics.comments.description")}
                    value={formatNumber(m.comments_count ?? m.comments)}
                    subtitle={`за ${presetLabel}`}
                  />
                </Col>
                <Col xs={12} md={6}>
                  <StatCard
                    icon={<HeartOutlined />}
                    title={t("mediaInsights.metrics.likes.title")}
                    help={t("mediaInsights.metrics.likes.description")}
                    value={formatNumber(m.like_count ?? m.likes)}
                    subtitle={`за ${presetLabel}`}
                  />
                </Col>
              </Row>
            )}

            <Row gutter={[16, 16]} style={{ marginBottom: 12 }}>
              <Col xs={24}>
                <Card size="small" bordered={false} style={{ borderRadius: 12 }} title={t("mediaInsights.rangeTitle")}>
                  <Text>
                    c {dayjs(range.from).format("DD.MM.YYYY")} по {dayjs(range.to).format("DD.MM.YYYY")}
                  </Text>
                </Card>
              </Col>
            </Row>

            <Row gutter={[16, 16]}>
              <Col xs={24}>
                <Card size="small" bordered={false} style={{ borderRadius: 12 }} title={t("mediaInsights.likesByDay")}>
                  <LineChart
                    label="Likes"
                    data={seriesLikes}
                    xKey="date"
                    yKey="value"
                    color="#6d28d9"
                    formatTooltipValue={(v) => `${v ?? "-"}`}
                  />
                </Card>
              </Col>

              <Col xs={24}>
                <Card size="small" bordered={false} style={{ borderRadius: 12 }} title={t("mediaInsights.commentsByDay")}>
                  <LineChart
                    label="Comments"
                    data={seriesComments}
                    xKey="date"
                    yKey="value"
                    color="#3b82f6"
                    formatTooltipValue={(v) => `${v ?? "-"}`}
                  />
                </Card>
              </Col>
            </Row>
          </Spin>
        </Content>
      </Layout>
    </>
  );
}

export default MediaInsightsPage;
